/**
 * Crypto primitives on Web standards only.
 *
 * The same code runs in Node 22, Cloudflare Workers and Deno, so nothing here
 * imports `node:crypto`. Everything is async because WebCrypto is.
 */
import { PhinestError } from './errors.js';
const enc = new TextEncoder();
const dec = new TextDecoder();
function subtle() {
    const c = globalThis.crypto;
    if (!c?.subtle)
        throw new PhinestError('internal', 'WebCrypto is unavailable in this runtime.');
    return c.subtle;
}
// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------
export function toBase64(bytes) {
    let s = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(s);
}
export function fromBase64(b64) {
    const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++)
        out[i] = s.charCodeAt(i);
    return out;
}
export const toBase64Url = (bytes) => toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
export function toHex(bytes) {
    let s = '';
    for (const b of bytes)
        s += b.toString(16).padStart(2, '0');
    return s;
}
/** Random uuid v4. Available as a global in every runtime we target. */
export function randomId() {
    return globalThis.crypto.randomUUID();
}
export function randomBytes(n) {
    const b = new Uint8Array(n);
    globalThis.crypto.getRandomValues(b);
    return b;
}
function bytes(v) {
    if (typeof v === 'string')
        return enc.encode(v);
    return v instanceof ArrayBuffer ? new Uint8Array(v) : v;
}
export async function sha256(data) {
    return new Uint8Array(await subtle().digest('SHA-256', bytes(data)));
}
export async function sha256Hex(data) {
    return toHex(await sha256(data));
}
export async function hmacSha256(secret, message) {
    const key = await subtle().importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    return new Uint8Array(await subtle().sign('HMAC', key, enc.encode(message)));
}
export async function hmacSha256Base64Url(secret, message) {
    return toBase64Url(await hmacSha256(secret, message));
}
/** Length-independent comparison that does not leak which byte differed. */
export function constantTimeEqual(a, b) {
    const x = enc.encode(a);
    const y = enc.encode(b);
    let diff = x.length ^ y.length;
    const n = Math.max(x.length, y.length);
    for (let i = 0; i < n; i++)
        diff |= (x[i] ?? 0) ^ (y[i] ?? 0);
    return diff === 0;
}
// ---------------------------------------------------------------------------
// Authenticated encryption for integration credentials at rest
// Format: v2.<iv base64url>.<ciphertext+tag base64url>
// The key is SHA-256 of the configured secret, so any secret length works.
// ---------------------------------------------------------------------------
const CIPHER_VERSION = 'v2';
async function aesKey(secret, usage) {
    if (!secret)
        throw new PhinestError('internal', 'No encryption secret configured (PHINEST_CREDENTIALS_KEY).');
    const raw = await sha256(secret);
    return subtle().importKey('raw', raw, { name: 'AES-GCM' }, false, usage);
}
export async function encryptJson(secret, value) {
    const iv = randomBytes(12);
    const key = await aesKey(secret, ['encrypt']);
    const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: iv }, key, enc.encode(JSON.stringify(value))));
    return `${CIPHER_VERSION}.${toBase64Url(iv)}.${toBase64Url(ct)}`;
}
export async function decryptJson(secret, cipherText) {
    const parts = String(cipherText ?? '').split('.');
    if (parts.length !== 3 || parts[0] !== CIPHER_VERSION) {
        throw new PhinestError('internal', `Unrecognised credential format (expected ${CIPHER_VERSION}).`);
    }
    const key = await aesKey(secret, ['decrypt']);
    try {
        const pt = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(parts[1]) }, key, fromBase64(parts[2]));
        return JSON.parse(dec.decode(pt));
    }
    catch {
        throw new PhinestError('internal', 'Stored credentials could not be decrypted. PHINEST_CREDENTIALS_KEY has probably changed; reconnect the integration.');
    }
}
//# sourceMappingURL=crypto.js.map